import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

/**
 * Two-step referral consent dialog.
 *
 * The customer completes the full quote form (Step 1). When they click
 * "Continue" the form calls onOpenChange(true) which opens this dialog.
 * ONLY inside the dialog do we reveal the specific provider name.
 * The customer must actively tick the (unticked-by-default) checkbox and
 * press "Request my quote" before referral data is transmitted.
 *
 * Props:
 *   open              — controlled open state
 *   onOpenChange      — setter for open state
 *   providerName      — plain-text provider name (revealed here only)
 *   headline          — dialog headline ("Confirm your quote request")
 *   summary           — one-sentence description of what will be shared
 *   checkboxLabel     — full consent copy shown next to the checkbox
 *   submitting        — external "in flight" state to disable the button
 *   onConfirm         — called only when checkbox is ticked AND user clicks the CTA
 *   testidPrefix      — for data-testid attributes (e.g. "conveyancing-consent")
 */
const PartnerConsentDialog = ({
  open,
  onOpenChange,
  providerName,
  headline,
  summary,
  checkboxLabel,
  submitting = false,
  onConfirm,
  testidPrefix,
}) => {
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!open) setConsent(false); // reset if the customer backs out
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden sm:max-w-lg"
        data-testid={`${testidPrefix}-dialog`}
      >
        <div className="bg-[#F7FAF9] px-6 pt-6 pb-4 border-b border-[#DCE7E3]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0F766E]">
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Final step
          </div>
          <DialogTitle className="font-sora text-xl sm:text-2xl font-semibold text-[#0B1220] mt-2">
            {headline}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-[#475569] leading-relaxed">
            Your details will be securely shared with{" "}
            <span className="text-[#0B1220] font-semibold">{providerName}</span>
            {summary ? `, ${summary}` : ""}.
          </DialogDescription>
        </div>

        <div className="px-6 py-5">
          <label className="flex gap-3 items-start text-sm text-[#0B1220] leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#0F766E] shrink-0"
              data-testid={`${testidPrefix}-checkbox`}
            />
            <span>{checkboxLabel}</span>
          </label>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid={`${testidPrefix}-cancel`}
              className="text-sm font-medium text-[#475569] hover:text-[#0B1220] px-4 py-2.5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!consent || submitting}
              data-testid={`${testidPrefix}-confirm`}
              className="mp-btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                "Request my quote"
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerConsentDialog;
